import type { RealtimePostgresChangesPayload } from '@supabase/supabase-js';
import { createClient } from '@/lib/supabase/client';

type RealtimeEvent = '*' | 'INSERT' | 'UPDATE' | 'DELETE';

export type UserRealtimeSpec = {
  table: string;
  filter?: string;
  event?: RealtimeEvent;
  schema?: string;
  key?: string;
};

type ResolvedUserRealtimeSpec = {
  key: string;
  table: string;
  filter?: string;
  event: RealtimeEvent;
  schema: string;
};

type UserRealtimeListener = {
  callback: (
    change: RealtimePostgresChangesPayload<Record<string, unknown>>,
    spec: ResolvedUserRealtimeSpec
  ) => void;
  specKeys: Set<string>;
};

type RealtimeChannelLike = {
  on: (
    type: 'postgres_changes',
    filter: {
      event: RealtimeEvent;
      schema: string;
      table: string;
      filter?: string;
    },
    callback: (change: RealtimePostgresChangesPayload<Record<string, unknown>>) => void
  ) => RealtimeChannelLike;
  subscribe: () => void;
};

type UserRealtimeState = {
  channel: RealtimeChannelLike | null;
  listeners: Map<string, UserRealtimeListener>;
  specByKey: Map<string, ResolvedUserRealtimeSpec>;
  refCountBySpecKey: Map<string, number>;
  rebuildVersion: number;
};

const userRealtimeStates = new Map<string, UserRealtimeState>();

export function isSupabaseRealtimeEnabled() {
  return false;
}

function getRealtimeClient() {
  return createClient();
}

function buildSpecKey(spec: Omit<ResolvedUserRealtimeSpec, 'key'>) {
  return `${spec.schema}:${spec.table}:${spec.event}:${spec.filter || ''}`;
}

function resolveSpec(spec: UserRealtimeSpec): ResolvedUserRealtimeSpec {
  const resolved = {
    table: spec.table,
    filter: spec.filter,
    event: spec.event || '*',
    schema: spec.schema || 'public',
  } satisfies Omit<ResolvedUserRealtimeSpec, 'key'>;

  return {
    ...resolved,
    key: spec.key || buildSpecKey(resolved),
  };
}

function getUserRealtimeState(userId: string): UserRealtimeState {
  let state = userRealtimeStates.get(userId);

  if (!state) {
    state = {
      channel: null,
      listeners: new Map(),
      specByKey: new Map(),
      refCountBySpecKey: new Map(),
      rebuildVersion: 0,
    };
    userRealtimeStates.set(userId, state);
  }

  return state;
}

function dispatchUserRealtime(
  state: UserRealtimeState,
  specKey: string,
  change: RealtimePostgresChangesPayload<Record<string, unknown>>
) {
  const spec = state.specByKey.get(specKey);
  if (!spec) return;

  state.listeners.forEach((listener) => {
    if (!listener.specKeys.has(specKey)) return;
    listener.callback(change, spec);
  });
}

async function rebuildUserRealtimeChannel(userId: string, state: UserRealtimeState) {
  const client = getRealtimeClient();
  const version = state.rebuildVersion + 1;
  state.rebuildVersion = version;

  const previousChannel = state.channel;
  state.channel = null;

  if (previousChannel) {
    try {
      await client.removeChannel(previousChannel);
    } catch {
      // Ignore channel teardown failures during rapid subscription churn.
    }
  }

  if (version !== state.rebuildVersion) {
    return;
  }

  if (state.specByKey.size === 0) {
    if (state.listeners.size === 0) {
      userRealtimeStates.delete(userId);
    }
    return;
  }

  const channel = client.channel(
    `user-realtime-${userId}-${version}`
  ) as unknown as RealtimeChannelLike;

  state.specByKey.forEach((spec, specKey) => {
    const handleChange: (
      change: RealtimePostgresChangesPayload<Record<string, unknown>>
    ) => void = (change) => {
      dispatchUserRealtime(state, specKey, change);
    };

    channel.on(
      'postgres_changes',
      {
        event: spec.event,
        schema: spec.schema,
        table: spec.table,
        ...(spec.filter ? { filter: spec.filter } : {}),
      },
      handleChange
    );
  });

  channel.subscribe();

  if (version !== state.rebuildVersion) {
    try {
      await client.removeChannel(channel);
    } catch {
      // Ignore stale channel cleanup failures.
    }
    return;
  }

  state.channel = channel;
}

export function subscribeToUserRealtime(
  userId: string,
  specs: UserRealtimeSpec[],
  callback: UserRealtimeListener['callback']
) {
  if (!isSupabaseRealtimeEnabled()) {
    return () => {};
  }

  if (!userId || specs.length === 0) {
    return () => {};
  }

  const state = getUserRealtimeState(userId);
  const listenerId = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  const resolvedSpecs = specs.map(resolveSpec);

  state.listeners.set(listenerId, {
    callback,
    specKeys: new Set(resolvedSpecs.map((spec) => spec.key)),
  });

  resolvedSpecs.forEach((spec) => {
    state.specByKey.set(spec.key, spec);
    state.refCountBySpecKey.set(spec.key, (state.refCountBySpecKey.get(spec.key) || 0) + 1);
  });

  void rebuildUserRealtimeChannel(userId, state);

  return () => {
    const currentState = userRealtimeStates.get(userId);
    if (!currentState) return;

    currentState.listeners.delete(listenerId);

    resolvedSpecs.forEach((spec) => {
      const nextCount = (currentState.refCountBySpecKey.get(spec.key) || 0) - 1;

      if (nextCount <= 0) {
        currentState.refCountBySpecKey.delete(spec.key);
        currentState.specByKey.delete(spec.key);
      } else {
        currentState.refCountBySpecKey.set(spec.key, nextCount);
      }
    });

    void rebuildUserRealtimeChannel(userId, currentState);
  };
}
